import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function StudentSignUp() {
  const navigate = useNavigate();
  const { signupStudent } = useAuth();
  const [form, setForm] = useState({ name: '', phone: '', password: '', class_level: 6, board: 'CBSE', school_name: '', city: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password) { toast.error('Please fill required fields'); return; }
    if (form.phone.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (form.password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setLoading(true);
    try {
      const data = await signupStudent({ ...form, class_level: parseInt(form.class_level) });
      toast.success(`Welcome, ${data.user.name}! Your Student Code: ${data.student_code}`);
      navigate('/home', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-4 py-6" style={{ background: '#FFF8F0' }}>
      <button data-testid="student-signup-back" onClick={() => navigate('/')} className="self-start mb-4">
        <ArrowLeft size={24} color="#1B4F8A" />
      </button>

      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-6">
          <span className="text-4xl block mb-1">🎓</span>
          <h1 className="font-fredoka text-2xl" style={{ color: '#FF6B35' }}>Join MathTopper!</h1>
          <p className="text-sm font-semibold" style={{ color: '#8B7355' }}>Start your math journey today</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Full Name *</label>
            <input data-testid="student-name-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Enter your name"
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Mobile Number *</label>
            <div className="flex gap-2">
              <span className="bg-white border-2 rounded-2xl px-3 py-3 font-bold text-xs flex items-center" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>+91</span>
              <input data-testid="student-phone-input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="9876543210" maxLength={10}
                className="flex-1 bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Password *</label>
            <div className="relative">
              <input data-testid="student-password-input" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Create a password"
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all pr-12" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5" style={{ color: '#8B7355' }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Class *</label>
              <select data-testid="student-class-select" value={form.class_level} onChange={e => set('class_level', e.target.value)}
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>
                {[6, 7, 8, 9, 10].map(c => <option key={c} value={c}>Class {c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>Board *</label>
              <select data-testid="student-board-select" value={form.board} onChange={e => set('board', e.target.value)}
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>
                <option value="CBSE">CBSE</option>
                <option value="ICSE">ICSE</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>School Name (optional)</label>
            <input data-testid="student-school-input" value={form.school_name} onChange={e => set('school_name', e.target.value)} placeholder="Your school name"
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: '#1B4F8A' }}>City (optional)</label>
            <input data-testid="student-city-input" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Your city"
              className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          </div>

          <button data-testid="student-signup-submit" type="submit" disabled={loading}
            className="btn-press w-full py-4 rounded-full font-extrabold text-white text-base uppercase tracking-wide mt-2"
            style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Creating Account...' : 'Start Learning!'}
          </button>
        </form>

        <p className="text-center text-xs font-semibold mt-4" style={{ color: '#8B7355' }}>
          Already have an account? <button onClick={() => navigate('/login')} className="underline" style={{ color: '#1B4F8A' }} data-testid="student-to-login">Login</button>
        </p>
      </div>
    </div>
  );
}
